import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  NamensmatrixModalComponent,
  MatrixViewState,
  NamensmatrixNavState,
} from './namensmatrix-modal.component';

/**
 * Öffnet die Namensmatrix als Modal und regelt das Zurück-Verhalten.
 *
 * Ablauf:
 *  - {@link open} öffnet die Matrix aus dem aktuellen Aufrufer (Viewer/Editor)
 *    und merkt sich dessen URL als Ursprung.
 *  - Ein Klick auf eine Zelle (im Modal) navigiert auf die HBF-Seite und legt
 *    `fromMatrix` + Filterzustand + Ursprung in den History-State.
 *  - {@link reopenIfFromCell} wird vom „Zurück"-Knopf des Viewers aufgerufen:
 *    kam der aktuelle Eintrag aus einem Zellklick, öffnet es die Matrix mit
 *    gleicher Sicht wieder (statt zurückzublättern). Der Marker wird dabei
 *    EINMALIG verbraucht -> ein weiteres „Zurück" geht regulär zum Aufrufer
 *    (sonst säße der Nutzer in einer Schleife und käme nie aus dem Viewer raus).
 *  - Schließt man die Matrix OHNE Zellklick (X/Esc/Backdrop/„Schließen"), geht
 *    es zurück zum Ursprung. Beim Erstaufruf ist der Ursprung die aktuelle URL,
 *    die Navigation dorthin also ein No-Op (`onSameUrlNavigation: 'ignore'`).
 */
@Injectable({ providedIn: 'root' })
export class NamensmatrixService {
  private modalService = inject(NgbModal);
  private router = inject(Router);

  /** Matrix aus dem aktuellen Aufrufer öffnen (Erstaufruf). */
  open(): void {
    this.openWith(this.router.url, undefined);
  }

  /**
   * Vom Viewer-„Zurück" aufgerufen. Stammt der aktuelle History-Eintrag von
   * einem Matrix-Zellklick, wird die Matrix mit gespeicherter Sicht wieder
   * geöffnet und `true` zurückgegeben; sonst `false` (normales Zurück greift).
   */
  reopenIfFromCell(): boolean {
    const st = window.history.state as Partial<NamensmatrixNavState> | null;
    if (!st || !st.fromMatrix) return false;
    // Marker EINMALIG verbrauchen: Ohne das bliebe `fromMatrix` dauerhaft auf
    // diesem History-Eintrag kleben; jedes weitere „Zurück" würde die Matrix
    // erneut öffnen und der Nutzer käme nie aus dem Viewer heraus. Nach dem ersten
    // Wiederöffnen ist der Eintrag ein normaler Steckbrief -> das nächste „Zurück"
    // greift regulär (location.back zum Aufrufer). Nur unseren Marker entfernen,
    // Angulars restlichen History-State (u.a. navigationId) unangetastet lassen.
    window.history.replaceState({ ...window.history.state, fromMatrix: false }, '');
    this.openWith(st.matrixOrigin || this.router.url, st.matrixView);
    return true;
  }

  private openWith(originUrl: string, initialState: MatrixViewState | undefined): void {
    const ref = this.modalService.open(NamensmatrixModalComponent, { size: 'xl', scrollable: true });
    const instance = ref.componentInstance as NamensmatrixModalComponent;
    instance.originUrl = originUrl;
    if (initialState) instance.initialState = initialState;
    // close() (Zellklick) -> resolve: onCell hat bereits navigiert, nichts tun.
    // dismiss() (X/Esc/Backdrop/„Schließen") -> reject: zurück zum Ursprung.
    // replaceUrl, damit der Rückweg keinen zusätzlichen History-Eintrag stapelt;
    // beim Erstaufruf ist der Ursprung die aktuelle URL (Navigation = No-Op).
    ref.result.then(
      () => { /* via Zellklick geschlossen */ },
      () => { void this.router.navigateByUrl(originUrl, { replaceUrl: true }); },
    );
  }
}
